import { useQueryClient } from "@tanstack/react-query";
import { BottomSheet } from "./BottomSheet";
import { useChannels } from "../hooks/channels";
import { useAppStore } from "../store/app";
import { haptics } from "../lib/telegram";

/** Bottom-sheet channel picker — the multi-channel switcher. Lists every channel
 *  the user belongs to; admin channels carry a badge. */
export function ChannelSwitcher() {
  const open = useAppStore((s) => s.channelSwitcherOpen);
  const setOpen = useAppStore((s) => s.setChannelSwitcherOpen);
  const activeId = useAppStore((s) => s.activeChannelId);
  const setActive = useAppStore((s) => s.setActiveChannel);
  const { data: channels = [] } = useChannels();
  const qc = useQueryClient();

  function pick(id: string) {
    haptics.select();
    setActive(id);
    setOpen(false);
    // Channel scope changed — drop the previous channel's cached lists.
    qc.invalidateQueries({ queryKey: ["browse"] });
    qc.invalidateQueries({ queryKey: ["search"] });
    qc.invalidateQueries({ queryKey: ["collections"] });
    qc.invalidateQueries({ queryKey: ["new-since"] });
  }

  return (
    <BottomSheet open={open} onClose={() => setOpen(false)} title="Switch channel">
      {channels.length === 0 ? (
        <p className="py-6 text-center text-sm text-tg-hint">No channels yet.</p>
      ) : (
        <ul className="space-y-1 pb-2">
          {channels.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => pick(c.id)}
                className={
                  "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left active:scale-[0.98] " +
                  (c.id === activeId ? "bg-tg-button/15" : "")
                }
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-tg-bg-section text-sm font-semibold text-tg-text">
                  {c.title.slice(0, 1).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-tg-text">{c.title}</span>
                  {c.username && <span className="block truncate text-xs text-tg-hint">@{c.username}</span>}
                </span>
                {c.is_admin && (
                  <span className="rounded-md bg-tg-bg-section px-1.5 py-0.5 text-[10px] font-medium text-tg-accent">
                    Admin
                  </span>
                )}
                {c.id === activeId && <span className="text-tg-accent">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </BottomSheet>
  );
}
